import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { GamePlayer } from "@/hooks/useGameAuth";
import { useGameAdmin } from "@/hooks/useGameAdmin";
import { Gamepad2, LayoutDashboard, Store, Trophy, Zap, Map, LogOut, Gift, Shield } from "lucide-react";

interface GameLayoutProps {
  children: React.ReactNode;
  player: GamePlayer | null;
  onSignOut: () => void;
}

export function GameLayout({ children, player, onSignOut }: GameLayoutProps) {
  const location = useLocation();
  const { isAdmin } = useGameAdmin();

  const balance = player ? Number(player.virtual_balance) : 0;

  const navLinks = [
    { path: "/game/marketplace", label: "Market", icon: Store },
    { path: "/game/mint", label: "Mint", icon: Zap },
    { path: "/game/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/game/leaderboard", label: "Ranks", icon: Trophy },
    { path: "/game/invite", label: "Invite", icon: Gift },
    { path: "/game/roadmap", label: "Roadmap", icon: Map },
    ...(isAdmin ? [{ path: "/game/admin", label: "Admin", icon: Shield }] : []),
  ];

  return (
    <div className="h-dvh overflow-hidden game-bg flex flex-col text-white">
      {/* Header */}
      <header className="shrink-0 border-b border-purple-500/20 bg-black/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/game" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-game-neon flex items-center justify-center group-hover:shadow-game-neon-sm transition-shadow">
              <Gamepad2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-extrabold text-lg text-game-gradient">
              FLOW Game
            </span>
          </Link>

          {player && (
            <div className="flex items-center gap-2">
              <nav className="hidden md:flex items-center gap-1">
                {navLinks.map((link) => (
                  <Link key={link.path} to={link.path}>
                    <Button
                      variant={location.pathname === link.path ? "default" : "ghost"}
                      size="sm"
                      className={`gap-1.5 ${
                        location.pathname === link.path
                          ? "btn-game-neon border-0"
                          : "text-gray-400 hover:text-white hover:bg-purple-500/10"
                      }`}
                    >
                      <link.icon className="w-4 h-4" />
                      <span className="hidden lg:inline">{link.label}</span>
                    </Button>
                  </Link>
                ))}
              </nav>

              <div className="flex items-center gap-2 ml-2 border-l border-purple-500/20 pl-2">
                <span className="text-sm font-bold text-game-cyan" style={{ textShadow: '0 0 8px rgba(6, 182, 212, 0.5)' }}>
                  ${Number(player.virtual_balance).toFixed(2)}
                </span>
                <span className="text-xs text-gray-500 hidden sm:inline">
                  {player.display_name}
                </span>
                <Button variant="ghost" size="icon" onClick={onSignOut} className="h-8 w-8 text-gray-400 hover:text-white hover:bg-purple-500/10">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile nav */}
        {player && (
          <div className="md:hidden flex border-t border-purple-500/20 overflow-x-auto scrollbar-hide px-3 gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`shrink-0 flex items-center gap-1.5 py-2.5 px-2 text-xs font-semibold transition-colors whitespace-nowrap ${
                  location.pathname === link.path
                    ? "text-game-pink border-b-2 border-pink-500 bg-pink-500/5"
                    : "text-gray-500"
                }`}
              >
                <link.icon className="w-3.5 h-3.5" />
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </header>

      {/* Content */}
      <main className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">{children}</main>

      {/* Footer */}
      <footer className="shrink-0 border-t border-purple-500/20 py-4 text-center text-xs text-gray-600">
        FLOW Game •{" "}
        <Link to="/" className="text-game-pink hover:underline">
          Back to FLOWnft
        </Link>
      </footer>
    </div>
  );
}
